"use client";
import { Card, CardContent, CardHeader } from "./card-pricing";
import { Sparkles as SparklesComp } from "./sparkles-pricing";
import { TimelineContent } from "./timeline-content-pricing";
import { VerticalCutReveal } from "./vertical-cut-reveal-pricing";
import { cn } from "../../lib/utils";
import NumberFlow from "@number-flow/react";
import { motion } from "framer-motion";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import "./pricing-section.css";


const PricingSwitch = ({ onSwitch }: { onSwitch: (value: string) => void }) => {
  const [selected, setSelected] = useState("0");

  const handleSwitch = (value: string) => {
    setSelected(value);
    onSwitch(value);
  };

  return (
    <div className="tw-flex tw-justify-center">
      <div className="tw-relative tw-z-10 tw-mx-auto tw-flex tw-w-fit tw-rounded-full tw-bg-neutral-900 tw-border tw-border-gray-700 tw-p-1">
        <button
          onClick={() => handleSwitch("0")}
          className={cn(
            "tw-relative tw-z-10 tw-w-fit tw-h-10 tw-rounded-full sm:tw-px-6 tw-px-3 sm:tw-py-2 tw-py-1 tw-font-medium tw-transition-colors tw-duration-200",
            selected === "0" ? "tw-text-white" : "tw-text-gray-200",
          )}
        >
          {selected === "0" && (
            <motion.span
              className="tw-absolute tw-top-0 tw-left-0 tw-h-10 tw-w-full tw-rounded-full tw-border-4 tw-shadow-sm tw-shadow-blue-600 tw-border-blue-600 tw-bg-gradient-to-t tw-from-blue-500 tw-to-blue-600"
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.2, ease: "easeOut" }}
            />
          )}
          <span className="tw-relative">Individuel</span>
        </button>

        <button
          onClick={() => handleSwitch("1")}
          className={cn(
            "tw-relative tw-z-10 tw-w-fit tw-h-10 tw-flex-shrink-0 tw-rounded-full sm:tw-px-6 tw-px-3 sm:tw-py-2 tw-py-1 tw-font-medium tw-transition-colors tw-duration-200",
            selected === "1" ? "tw-text-white" : "tw-text-gray-200",
          )}
        >
          {selected === "1" && (
            <motion.span
              className="tw-absolute tw-top-0 tw-left-0 tw-h-10 tw-w-full tw-rounded-full tw-border-4 tw-shadow-sm tw-shadow-blue-600 tw-border-blue-600 tw-bg-gradient-to-t tw-from-blue-500 tw-to-blue-600"
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.2, ease: "easeOut" }}
            />
          )}
          <span className="tw-relative tw-flex tw-items-center tw-gap-2">Groupe de 5 et +</span>
        </button>

        <button
          onClick={() => handleSwitch("2")}
          className={cn(
            "tw-relative tw-z-10 tw-w-fit tw-h-10 tw-flex-shrink-0 tw-rounded-full sm:tw-px-6 tw-px-3 sm:tw-py-2 tw-py-1 tw-font-medium tw-transition-colors tw-duration-200",
            selected === "2" ? "tw-text-white" : "tw-text-gray-200",
          )}
        >
          {selected === "2" && (
            <motion.span
              className="tw-absolute tw-top-0 tw-left-0 tw-h-10 tw-w-full tw-rounded-full tw-border-4 tw-shadow-sm tw-shadow-blue-600 tw-border-blue-600 tw-bg-gradient-to-t tw-from-blue-500 tw-to-blue-600"
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.2, ease: "easeOut" }}
            />
          )}
          <span className="tw-relative tw-flex tw-items-center tw-gap-2">Groupe de 10</span>
        </button>
      </div>
    </div>
  );
};

interface PricingSectionAzureAdmProps {
  openContactModal?: () => void;
}


export default function PricingSectionAzureAdm({ openContactModal }: PricingSectionAzureAdmProps) {
  const { t } = useTranslation();
  const [pricingTier, setPricingTier] = useState(0); // 0: individual, 1: group 5+, 2: group 10+
  const pricingRef = useRef<HTMLDivElement>(null);

  const plans = [
    {
      name: t('pricingFormationAD.ressources.title', "Pack Ressources"),
      description: t('pricingFormationAD.ressources.description', "Accès IT Monkeys Academy et toutes les ressources du programme"),
      price: 1650,
      groupPrice5: 1320,
      groupPrice10: 1155,
      buttonText: t('pricingFormationAD.common.contactButton', "Nous contacter"),
      buttonVariant: "outline" as const,
      hasGlowEffect: true,
      includes: [
        t('pricingFormationAD.ressources.features.1', "Accès à vie aux ressources"),
        t('pricingFormationAD.ressources.features.2', "Vidéos de formation HD"),
        t('pricingFormationAD.ressources.features.3', "Labs pas à pas interactifs"), 
        t('pricingFormationAD.ressources.features.4', "Documentation technique complète"),
        t('pricingFormationAD.ressources.features.5', "Support communauté 24/7"),
      ],
    },
    {
      name: t('pricingFormationAD.distanciel.title', "Distanciel"),
      description: t('pricingFormationAD.distanciel.description', "Formation premium à distance dispensée par des experts hautement certifiés"),
      price: 6600,
      groupPrice5: 5280,
      groupPrice10: 4620,
      buttonText: t('pricingFormationAD.common.contactButton', "Nous contacter"),
      buttonVariant: "default" as const,
      popular: true,
      hasGlowEffect: false,
      includes: [
        t('pricingFormationAD.distanciel.duration', "Formation à distance en 22 heures"),
        t('pricingFormationAD.distanciel.theoretical', "Connaissances théoriques + labs"),
        t('pricingFormationAD.distanciel.theoretical', "Experts Microsoft certifiés"),
        t('pricingFormationAD.distanciel.practical', "Démos et mise en application"),
        t('pricingFormationAD.presentiel.features.1', "Administration Entra ID (Azure AD)"),

      ],
    },
    {
      name: t('pricingFormationAD.presentiel.title', "Présentiel"),
      description: t('pricingFormationAD.presentiel.description', "Formation premium sur site dispensée par des experts hautement certifiés"),
      price: 7230,
      groupPrice5: 5784,
      groupPrice10: 5061,
      buttonText: t('pricingFormationAD.common.contactButton', "Nous contacter"),
      buttonVariant: "outline" as const,
      hasGlowEffect: false,
      includes: [
        t('pricingFormationAD.presentiel.duration', "Formation sur site en 22 heures"),
        t('pricingFormationAD.presentiel.theoretical', "Connaissances théoriques + labs"),
        t('pricingFormationAD.presentiel.practical', "Démos et mise en application"),
        t('pricingFormationAD.presentiel.features.1', "Administration Entra ID (Azure AD)"),
      ],
    },
  ];

  const revealVariants = {
    visible: (i: number) => ({
      y: 0,
      opacity: 1,
      filter: "blur(0px)",
      transition: {
        delay: i * 0.1,
        duration: 0.2,
      },
    }),
    hidden: {
      filter: "blur(10px)",
      y: -20,
      opacity: 0,
    },
  };

  const togglePricingPeriod = (value: string) =>
    setPricingTier(Number.parseInt(value));

  return (
    <div
      className="tw-mx-auto tw-relative tw-bg-black tw-overflow-hidden "
      ref={pricingRef}
    >
      <TimelineContent
        animationNum={0}
        timelineRef={pricingRef}
        customVariants={revealVariants}
        once={true}
        className="tw-absolute tw-top-0 tw-h-96 tw-w-screen tw-overflow-hidden [mask-image:radial-gradient(50%_50%,white,transparent)]"
      >
        <div className="tw-absolute tw-bottom-0 tw-left-0 tw-right-0 tw-top-0 tw-bg-[linear-gradient(to_right,#ffffff2c_1px,transparent_1px),linear-gradient(to_bottom,#3a3a3a01_1px,transparent_1px)] tw-bg-[size:70px_80px]"></div>
        <SparklesComp
          density={1800}
          speed={1}
          color="#FFFFFF"
          className="tw-absolute tw-inset-x-0 tw-bottom-0 tw-h-full tw-w-full [mask-image:radial-gradient(50%_50%,white,transparent_85%)]"
        />
      </TimelineContent>
      <TimelineContent
        animationNum={0}
        timelineRef={pricingRef}
        customVariants={revealVariants}
        once={true}
        className="tw-absolute tw-left-0 tw-top-[-114px] tw-w-full tw-h-[100vh] tw-flex tw-flex-col tw-items-start tw-justify-start tw-content-start tw-flex-none tw-flex-nowrap tw-gap-2.5 tw-p-0 tw-z-0 tw-overflow-hidden tw-pointer-events-none"
      >
        <div className="framer-1i5axl2">
          <div
            className="tw-absolute tw-left-[-568px] tw-right-[-568px] tw-top-0 tw-h-[2053px] tw-flex-none tw-rounded-full"
            style={{
              border: "200px solid var(--gradient-color)",
              filter: "blur(92px)",
              WebkitFilter: "blur(92px)",
            }}
            data-border="true"
            data-framer-name="Ellipse 1"
          ></div>
          <div
            className="tw-absolute tw-left-[-568px] tw-right-[-568px] tw-top-0 tw-h-[2053px] tw-flex-none tw-rounded-full"
            style={{
              border: "200px solid var(--gradient-color)",
              filter: "blur(92px)",
              WebkitFilter: "blur(92px)",
            }}
            data-border="true"
            data-framer-name="Ellipse 2"
          ></div>
        </div>
      </TimelineContent>

      <article className="tw-text-center tw-mb-6 tw-pt-32 tw-max-w-3xl tw-mx-8 md:tw-mx-auto tw-space-y-2 tw-relative tw-z-50">
        <h2 className="tw-text-4xl tw-font-medium tw-text-white">
          <VerticalCutReveal
            splitBy="words"
            staggerDuration={0.05}
            staggerFrom="first"
            reverse={true}
            containerClassName="tw-justify-center tw-pb-[10px]"
            transition={{
              type: "spring",
              stiffness: 400,
              damping: 30,
              delay: 0,
            }}
          >
            Votre équipe sera en mesure de
          </VerticalCutReveal>
        </h2>

        <TimelineContent
          as="div"
          animationNum={1}
          timelineRef={pricingRef}
          customVariants={revealVariants}
          once={true}
          className="tw-text-gray-300 tw-text-center tw-pb-[40px] tw-flex tw-justify-center"
        >
          <ul className="tw-max-w-fit">
            <li className="tw-flex tw-items-center tw-gap-2">
              <span className="tw-h-2.5 tw-w-2.5 tw-bg-blue-500 tw-rounded-full"></span>
              <span>Déployer un socle Azure sécurisé en IaC</span>
            </li>
            <li className="tw-flex tw-items-center tw-gap-2">
              <span className="tw-h-2.5 tw-w-2.5 tw-bg-blue-500 tw-rounded-full"></span>
              <span>Gérer identités & conformité</span>
            </li>
            <li className="tw-flex tw-items-center tw-gap-2">
              <span className="tw-h-2.5 tw-w-2.5 tw-bg-blue-500 tw-rounded-full"></span>
              <span>Opérer monitoring + FinOps</span>
            </li>
            <li className="tw-flex tw-items-center tw-gap-2">
              <span className="tw-h-2.5 tw-w-2.5 tw-bg-blue-500 tw-rounded-full"></span>
              <span>Assurer sauvegarde + continuité + File Sync</span>
            </li>
          </ul>
        </TimelineContent>

        <TimelineContent
          as="div"
          animationNum={2}
          timelineRef={pricingRef}
          customVariants={revealVariants}
          once={true}
        >
          <PricingSwitch onSwitch={togglePricingPeriod} />
        </TimelineContent>
      </article>

      <div
        className="tw-absolute tw-top-0 tw-left-[10%] tw-right-[10%] tw-w-[80%] tw-h-full tw-z-0"
        style={{
          backgroundImage: `
        radial-gradient(circle at center, var(--gradient-color) 0%, transparent 70%)
      `,
          opacity: 0.6,
          mixBlendMode: "multiply",
        }}
      />

      {/* Centered blue gradient halo behind cards */}
      <div
        className="tw-absolute tw-top-[60%] tw-left-1/2 tw-transform tw--translate-x-1/2 tw--translate-y-1/2 tw-w-[1400px] tw-h-[800px] tw-z-10 tw-pointer-events-none"
        style={{
          background: `radial-gradient(ellipse at center, rgba(49, 49, 245, 0.6) 0%, rgba(49, 49, 245, 0.3) 40%, rgba(49, 49, 245, 0.1) 70%, transparent 100%)`,
          filter: "blur(70px)",
          borderRadius: "50%",
        }}
      />

      <div className="tw-grid md:tw-grid-cols-3 tw-max-w-5xl tw-gap-4 tw-pb-6 tw-mx-8 md:tw-mx-auto tw-relative tw-z-20">
        {plans.map((plan, index) => (
          <TimelineContent
            key={plan.name}
            as="div"
            animationNum={3 + index}
            timelineRef={pricingRef}
            customVariants={revealVariants}
            once={true}
          >
            <Card
              className={`tw-relative tw-text-white ${
                plan.popular
                  ? "tw-border-neutral-800 tw-bg-gradient-to-r tw-from-neutral-900 tw-via-neutral-800 tw-to-neutral-900 tw-shadow-[0px_-13px_300px_0px_#0900ff] tw-z-20"
                  : index === 0
                    ? "tw-border-neutral-800/80 tw-bg-gradient-to-r tw-from-neutral-900/45 tw-via-neutral-800/45 tw-to-neutral-900/45 tw-backdrop-blur-md tw-z-20"
                    : "tw-border-neutral-800 tw-bg-gradient-to-r tw-from-neutral-900 tw-via-neutral-800 tw-to-neutral-900 tw-z-10"
              }`}
            >
              <CardHeader className="tw-text-left">
                <div className="tw-flex tw-justify-between">
                  <h3 className="tw-text-3xl tw-mb-2">{plan.name}</h3>
                </div>
                <div className="tw-flex tw-items-baseline">
                  <>
                    <span className="tw-text-4xl tw-font-semibold ">
                      <NumberFlow
                        format={{
                          currency: "EUR",
                        }}
                        value={
                          pricingTier === 0 ? plan.price :
                          pricingTier === 1 ? plan.groupPrice5 :
                          plan.groupPrice10
                        }
                        className="tw-text-4xl tw-font-semibold"
                      />
                      €<sup className="tw-text-sm tw-text-gray-400 tw-ml-1">HT</sup>
                    </span>
                    <span className="tw-text-gray-300 tw-ml-2">
                      / personne
                    </span>
                  </>
                </div>

                {/* Pricing tier info */}
                <div className="tw-text-left tw-mb-2">
                  {pricingTier === 0 ? (
                    <span className="tw-text-sm tw-text-gray-400 tw-font-[550]">Tarif individuel</span>
                  ) : pricingTier === 1 ? (
                    <span className="tw-text-sm tw-text-green-400 tw-font-[550]">
                      Économisez {Math.round(((plan.price - plan.groupPrice5) / plan.price) * 100)}%
                    </span>
                  ) : (
                    <span className="tw-text-sm tw-text-green-400 tw-font-[550]">
                      Économisez {Math.round(((plan.price - plan.groupPrice10) / plan.price) * 100)}%
                    </span>
                  )}
                </div>

                <p className="tw-text-sm tw-text-gray-300 tw-mb-4 tw-font-[550]">{plan.description}</p>
              </CardHeader>

              <CardContent className="tw-pt-0">
                <button
                  onClick={() => {
                    if (plan.name.includes("Pack")) {
                      openContactModal?.();
                    }
                  }}
                  className={`tw-w-full tw-mb-6 tw-p-4 tw-text-xl tw-rounded-xl ${
                    plan.popular
                      ? "tw-bg-gradient-to-t tw-from-blue-500 tw-to-blue-600 tw-shadow-lg tw-shadow-blue-800 tw-border tw-border-blue-500 tw-text-white"
                      : index === 0
                        ? "tw-bg-gradient-to-t tw-from-neutral-950/50 tw-to-neutral-600/50 tw-backdrop-blur-sm tw-shadow-lg tw-shadow-neutral-900/30 tw-border tw-border-neutral-800/60 tw-text-white"
                        : plan.buttonVariant === "outline"
                          ? "tw-bg-gradient-to-t tw-from-neutral-950 tw-to-neutral-600 tw-shadow-lg tw-shadow-neutral-900 tw-border tw-border-neutral-800 tw-text-white"
                          : ""
                  }`}
                >
                  {plan.buttonText}
                </button>

                <div className="tw-space-y-3 tw-pt-4 tw-border-t tw-border-neutral-700">
                  <h4 className="tw-font-medium tw-text-base tw-mb-3">
                    {plan.includes[0]}
                  </h4>
                  <ul className="tw-space-y-2">
                    {plan.includes.slice(1).map((feature: string, featureIndex: number) => (
                      <li
                        key={featureIndex}
                        className="tw-flex tw-items-center tw-gap-2"
                      >
                        <span className="tw-h-2.5 tw-w-2.5 tw-bg-neutral-500 tw-rounded-full tw-grid tw-place-content-center"></span>
                        <span className="tw-text-sm tw-text-gray-300 tw-font-[550]">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </CardContent>
            </Card>
          </TimelineContent>
        ))}
      </div>
    </div>
  );
}